import { Buffer } from 'buffer';

import { ApolloClient, ApolloLink, concat, HttpLink } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

import { createCache } from './cache';
import { keycloakAccessToken, usesKeycloak } from '../keycloakSession';

// aws-appsync-subscription-link base64-encodes the realtime handshake with Node's Buffer; Amplify v6 no longer polyfills it.
(globalThis as { Buffer?: typeof Buffer }).Buffer ??= Buffer;

const url = process.env.NEXT_PUBLIC_API_URL!;
const region = process.env.NEXT_PUBLIC_AWS_REGION!;
const auth: AuthOptions = {
  type: 'AWS_IAM',
  credentials: async () => {
    const { credentials } = await fetchAuthSession();
    if (!credentials) {
      throw new Error('No AWS credentials: user is not signed in');
    }
    return credentials;
  }
};

const retry = new RetryLink({
  delay: {
    initial: 100,
    max: Infinity
  },
  attempts: {
    max: Infinity,
    retryIf: (error, _operation) => !!error
  }
});
const http = new HttpLink({ uri: url });
const httpLink = concat(retry, http);

const authLink = (auth: AuthOptions) =>
  ApolloLink.from([createAuthLink({ url, region, auth }), createSubscriptionHandshakeLink({ url, region, auth }, httpLink)]);
const link = ApolloLink.split(
  () => usesKeycloak(),
  authLink({ type: 'OPENID_CONNECT', jwtToken: keycloakAccessToken }),
  authLink(auth)
);

export default new ApolloClient({
  link,
  // The AWS_IAM auth link signs { operationName, variables, query }; the clientLibrary extension would break the SigV4 signature.
  enhancedClientAwareness: { transport: false },
  // Apollo 4 defaults this to true, which would raise `loading` on every refetch/fetchMore and flash the views' Backdrops.
  defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } },
  cache: createCache()
});
